/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState, PropsWithChildren } from "react";
import { useAuth0, User } from "@auth0/auth0-react";
import { When } from "react-if";
import { FlexContainer, Heading, theme, Button } from "@nordcloud/gnui";
import { Logo } from "~/components";
import { useSetupUserTracking } from "~/services/tracking";
import logo from "../assets/images/ninja.png";

type Props = {
  children: React.ReactNode;
};

export function Maintenance({ children }: Props) {
  // Additional state is used to allow verification if the maintenance screen is active for all users
  const [canBypass, setCanBypass] = useState(false);

  const { user } = useAuth0();
  const isPriviledgedUser = user?.email?.includes("@nordcloud.com") ?? false;

  const isMaintenance = process.env.REACT_APP_MAINTENANCE === "true";

  if (!isMaintenance || canBypass) {
    return <>{children}</>;
  }

  return (
    <Placeholder user={user}>
      <When condition={isPriviledgedUser}>
        <Button
          type="button"
          mt={theme.spacing.spacing04}
          onClick={() => setCanBypass(true)}
        >
          Let me in
        </Button>
      </When>
    </Placeholder>
  );
}

function Placeholder({
  user,
  children,
}: PropsWithChildren<{
  user: User | undefined;
}>) {
  useSetupUserTracking(user);

  return (
    <FlexContainer direction="column" pt="15vh">
      <img
        alt="logo"
        src={logo}
        css={{ width: "100%", height: "auto", maxWidth: "15rem" }}
      />
      <div>
        <Logo />
        <Heading level={2}>
          We&rsquo;re undergoing a bit of scheduled maintenance.
        </Heading>
        <Heading level={4} color={theme.colors.darks[2]} mb={1}>
          We&rsquo;ll be back up and running as fast as possible.
        </Heading>
        <Heading level={4} color={theme.colors.darks[2]}>
          Thank you for your patience.
        </Heading>
      </div>
      {children}
    </FlexContainer>
  );
}
